import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import {
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  LayoutDashboard,
  Users,
  Truck,
  Package,
  ClipboardList,
  Wallet,
  MapPin,
  FileSpreadsheet,
  Receipt,
  BadgeDollarSign,
  UserCircle,
  FileBarChart,
  BarChart3,
  FileText,
} from 'lucide-react';

const Sidebar = ({ isOpen: isOpenProp, setIsOpen: setIsOpenProp }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = isOpenProp !== undefined && setIsOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : internalOpen;
  const setIsOpen = isControlled ? setIsOpenProp : setInternalOpen;
  const [menuItems, setMenuItems] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (isControlled) return;

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isControlled, setIsOpen]);

  useEffect(() => {
    if (user) {
      const isAdmin = ['System Admin', 'Admin'].includes(user.role);

      const items = [
        {
          label: 'Dashboard',
          icon: LayoutDashboard,
          path:
            user.role === 'Accountant'
              ? '/accountant-dashboard'
              : user.role === 'Agent'
                ? '/agent-dashboard'
                : user.role === 'Driver'
                  ? '/driver-dashboard'
                  : '/admin-dashboard',
          visible: true,
          type: 'single',
        },
        {
          label: 'User Management',
          icon: Users,
          visible: ['System Admin', 'Admin', 'Accountant', 'Agent', 'Driver'].includes(user.role),
          type: 'dropdown',
          children: [
            {
              label: 'Users',
              path: '/users',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
            },
            {
              label: 'Clients & Vendors',
              path: '/shipment-clients',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent', 'Driver'].includes(user.role),
            },
            {
              label: 'Salary & Advances',
              path: '/salaries',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
            },
            {
              label: 'Attendance',
              path: '/attendance',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
            },
          ],
        },
        {
          label: 'Operations Management',
          icon: Truck,
          visible: isAdmin,
          type: 'dropdown',
          children: [
            { label: 'Vehicles', path: '/vehicles', visible: isAdmin },
            { label: 'Destination Hubs', path: '/location-hubs', visible: isAdmin },
          ],
        },
        {
          label: 'Booking Management',
          icon: Package,
          visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
          type: 'dropdown',
          children: [
            {
              label: 'Book Shipment',
              path: '/shipments',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
            },
            {
              label: 'Shipment Payment',
              path: '/payment-history',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
            },
            {
              label: 'Shipment Returns',
              path: '/returns',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
            },
            {
              label: 'Missing Shipments',
              path: '/missing',
              visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
            },
          ],
        },
        {
          label: 'Master Loading Sheet',
          icon: ClipboardList,
          path: '/ml-sheets',
          visible: true,
          type: 'single',
        },
        {
          label: 'Daily Earning',
          icon: Wallet,
          path: '/daily-earnings',
          visible: ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role),
          type: 'single',
        },
        {
          label: 'Track Shipment',
          icon: MapPin,
          path: '/track-shipment',
          visible: ['System Admin', 'Admin', 'Accountant', 'Agent', 'Driver'].includes(user.role),
          type: 'single',
        },
        {
          label: 'External ML Sheet',
          icon: FileSpreadsheet,
          path: '/external-ml',
          visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
          type: 'single',
        },
        {
          label: 'Expenses',
          icon: Receipt,
          path: '/expenses',
          visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
          type: 'single',
        },
        {
          label: 'Shipment Commissions',
          icon: BadgeDollarSign,
          visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
          type: 'dropdown',
          children: [
            {
              label: 'Commission Dashboard',
              path: '/commission-dashboard',
              visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
            },
            {
              label: 'Manage Commissions',
              path: '/commissions',
              visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
            },
            {
              label: 'Hub Insights',
              path: '/commissions/hub-insights',
              visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
            },
          ],
        },
        {
          label: 'Vehicle Owners',
          icon: UserCircle,
          path: '/vehicle-owners',
          visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
          type: 'single',
        },
        {
          label: 'MIS Reports',
          icon: FileBarChart,
          path: '/reports',
          visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
          type: 'single',
        },
        {
          label: 'Analytical Dashboard',
          icon: BarChart3,
          path: '/analytics',
          visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
          type: 'single',
        },
        {
          label: 'On Contract',
          icon: FileText,
          path: '/on-contracts',
          visible: ['System Admin', 'Admin', 'Accountant'].includes(user.role),
          type: 'single',
        },
      ];

      setMenuItems(items.filter((item) => item.visible));
    }
  }, [user]);

  useEffect(() => {
    if (user && isOpen) {
      const activeDropdown = menuItems.find((item) => {
        if (item.type === 'dropdown') {
          return item.children?.some((child) => child.visible && isActive(child.path));
        }
        return false;
      });

      if (activeDropdown && openDropdown !== activeDropdown.label) {
        setOpenDropdown(activeDropdown.label);
      }
    }
  }, [location.pathname, menuItems, user, isOpen]);

  const isActive = (path) => location.pathname.startsWith(path);

  const isDropdownActive = (children) =>
    children?.some((child) => child.visible && isActive(child.path));

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  const toggleDropdown = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const getItemClasses = (active) =>
    `w-full flex items-center gap-3 px-3 py-2.5 rounded-btn transition-all duration-200 text-sm font-medium border-l-[3px] ${
      active
        ? 'bg-brand-light text-brand border-brand font-semibold'
        : 'text-secondary border-transparent hover:bg-gray-50 hover:text-brand'
    }`;

  const getChildClasses = (active) =>
    `w-full flex items-center gap-2 px-3 py-2 rounded-btn transition-all duration-200 text-sm ${
      active
        ? 'text-brand font-semibold bg-brand-light/50'
        : 'text-gray-600 hover:text-brand hover:bg-gray-50'
    }`;

  if (loading || !user) {
    return (
      <aside className="w-16 lg:w-[265px] bg-white border-r border-gray-200 h-full fixed lg:relative left-0 top-0 z-20 shrink-0">
        <div className="p-3 space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-9 bg-gray-100 rounded-btn animate-pulse" />
          ))}
        </div>
      </aside>
    );
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile toggle — only when sidebar manages its own state (legacy pages) */}
      {!isControlled && !isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed top-3 left-3 z-[100] p-2 bg-white rounded-btn shadow-card border border-gray-100 lg:hidden"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5 text-secondary" />
        </button>
      )}

      <aside
        className={`
          transition-all duration-300 overflow-hidden shrink-0
          h-screen lg:h-full
          ${isOpen ? 'w-[280px] lg:w-[265px]' : 'w-0 lg:w-[72px]'}
          fixed lg:relative left-0 top-0 z-[99] lg:z-auto
          bg-white border-r border-gray-200
        `}
      >
        {/* Mobile close bar */}
        <div className="flex items-center justify-end p-3 border-b border-gray-100 lg:hidden">
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 rounded-btn hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-secondary" />
          </button>
        </div>

        <nav
          className={`
            p-2 lg:p-3 space-y-0.5 overflow-y-auto
            h-[calc(100vh-52px)] lg:h-full
            ${!isOpen ? 'hidden lg:block' : 'block'}
          `}
        >
          {menuItems.map((item) => {
            const Icon = item.icon;

            if (item.type === 'single') {
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={getItemClasses(active)}
                  title={item.label}
                >
                  {Icon && <Icon className="w-5 h-5 shrink-0" />}
                  {isOpen && <span className="truncate">{item.label}</span>}
                </button>
              );
            }

            if (item.type === 'dropdown') {
              const dropdownActive = isDropdownActive(item.children);
              const isDropdownOpen = openDropdown === item.label;

              return (
                <div key={item.label}>
                  <button
                    onClick={() => {
                      if (!isOpen) {
                        setIsOpen(true);
                        setTimeout(() => setOpenDropdown(item.label), 100);
                      } else {
                        toggleDropdown(item.label);
                      }
                    }}
                    className={getItemClasses(dropdownActive)}
                    title={item.label}
                  >
                    {Icon && <Icon className="w-5 h-5 shrink-0" />}
                    {isOpen && (
                      <>
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        <ChevronDown
                          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                            isDropdownOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </>
                    )}
                  </button>

                  {isOpen && isDropdownOpen && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">
                      {item.children
                        .filter((child) => child.visible)
                        .map((child) => {
                          const childActive = isActive(child.path);
                          return (
                            <button
                              key={child.path}
                              onClick={() => handleNavigation(child.path)}
                              className={getChildClasses(childActive)}
                              title={child.label}
                            >
                              <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">{child.label}</span>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </nav>
      </aside>
    </>
  );
};

export default React.memo(Sidebar);
